import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { ArrowLeft, MessageCircle, RefreshCw, Send, Loader2, AlertCircle, Bot, User, CheckCircle2, Copy, CheckCheck } from "lucide-react";

export default function AdminConversations() {
    const [info, setInfo] = useState(null);
    const [copied, setCopied] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all"); // all | open | escalated | resolved
    const [searchQ, setSearchQ] = useState("");

    const refreshConversations = useCallback(async () => {
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (searchQ.trim()) params.q = searchQ.trim();
            const [{ data: list }, { data: i }] = await Promise.all([
                api.get("/admin/whatsapp/conversations", { params }),
                api.get("/admin/whatsapp/info"),
            ]);
            setConversations(list);
            setInfo(i);
        } catch (e) {
            console.error("Load conversations failed:", e);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchQ]);

    useEffect(() => {
        refreshConversations();
        const t = setInterval(refreshConversations, 10000);
        return () => clearInterval(t);
    }, [refreshConversations]);

    const loadDetail = useCallback(async (convId) => {
        setSelected(convId);
        setDetail(null);
        try {
            const { data } = await api.get("/admin/whatsapp/conversations/detail", {
                params: { conversation_id: convId },
            });
            setDetail(data);
        } catch (e) {
            console.error("Load detail failed:", e);
        }
    }, []);

    const sendReply = async () => {
        if (!reply.trim() || !selected) return;
        setSending(true);
        try {
            await api.post("/admin/whatsapp/conversations/reply", {
                conversation_id: selected,
                body: reply,
            });
            setReply("");
            await loadDetail(selected);
            await refreshConversations();
        } catch (e) {
            alert(errorMessage(e));
        } finally {
            setSending(false);
        }
    };

    const markResolved = async () => {
        if (!selected) return;
        try {
            await api.post("/admin/whatsapp/conversations/resolve", null, {
                params: { conversation_id: selected },
            });
            await refreshConversations();
            await loadDetail(selected);
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div data-testid="admin-conversations-page" className="min-h-screen bg-background">
            <div className="border-b border-border bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
                    <div>
                        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors">
                            <ArrowLeft size={14} /> Dashboard
                        </Link>
                        <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight">WhatsApp Conversations</h1>
                        <p className="text-sm text-muted-foreground">AI agent menjawab otomatis · admin bisa intervensi kapan saja</p>
                    </div>
                    <button
                        data-testid="refresh-btn"
                        onClick={refreshConversations}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200"
                    >
                        <RefreshCw size={14} strokeWidth={1.75} /> Refresh
                    </button>
                </div>
            </div>

            {info && (
                <div className="mx-auto max-w-7xl px-5 pt-6">
                    <div data-testid="meta-info-card" className={`rounded-xl border p-4 sm:p-5 ${info.configured ? "border-accent/30 bg-accent/5" : "border-destructive/40 bg-destructive/5"}`}>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className={`label-eyebrow ${info.configured ? "text-accent" : "text-destructive"}`}>
                                    {info.configured ? "Meta WhatsApp Cloud API · Aktif" : "Meta WhatsApp Cloud API · Belum Di-setup"}
                                </span>
                                {info.configured && info.phone_number_id && (
                                    <span className="text-[11px] text-muted-foreground">
                                        Phone ID: <code className="rounded bg-white px-1.5 py-0.5 font-mono border border-border">{info.phone_number_id}</code>
                                    </span>
                                )}
                            </div>

                            {info.configured ? (
                                <p className="text-sm">
                                    Bot aktif via Meta Cloud API ({info.graph_api_version}). Pastikan webhook di Meta App Dashboard
                                    diarahkan ke URL di bawah ini, dan event <strong>messages</strong> ter-subscribe.
                                </p>
                            ) : (
                                <div className="text-sm space-y-2">
                                    <p className="font-medium text-destructive">
                                        Anda perlu mengisi kredensial Meta di <code className="rounded bg-white px-1 font-mono text-xs border border-border">backend/.env</code> sebelum bot bisa berjalan.
                                    </p>
                                    <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
                                        <li>Buka <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-primary underline">developers.facebook.com</a> → Create App tipe <em>Business</em></li>
                                        <li>Add Product → <strong>WhatsApp</strong> → catat <em>Phone Number ID</em> dan <em>Access Token</em></li>
                                        <li>Isi di <code className="rounded bg-white px-1 font-mono text-[11px] border border-border">META_WA_PHONE_NUMBER_ID</code>, <code className="rounded bg-white px-1 font-mono text-[11px] border border-border">META_WA_ACCESS_TOKEN</code>, <code className="rounded bg-white px-1 font-mono text-[11px] border border-border">META_WA_APP_SECRET</code> di backend/.env</li>
                                        <li>Restart backend, lalu setup webhook (lihat URL & token di bawah).</li>
                                    </ol>
                                </div>
                            )}

                            <div className="grid gap-3 sm:grid-cols-2">
                                <div className="rounded-lg border border-border bg-white p-3">
                                    <div className="label-eyebrow text-muted-foreground text-[10px]">Webhook Callback URL</div>
                                    <div className="mt-1 flex items-center gap-2">
                                        <code data-testid="webhook-url" className="flex-1 truncate font-mono text-xs">{info.webhook_url || info.webhook_path}</code>
                                        <button
                                            onClick={() => copyToClipboard(info.webhook_url || info.webhook_path)}
                                            data-testid="copy-webhook-btn"
                                            className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-1 text-[11px] hover:bg-secondary/80 transition-colors"
                                        >
                                            {copied ? <CheckCheck size={12} /> : <Copy size={12} />} {copied ? "Tersalin" : "Copy"}
                                        </button>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-border bg-white p-3">
                                    <div className="label-eyebrow text-muted-foreground text-[10px]">Verify Token</div>
                                    <div className="mt-1 text-xs">
                                        {info.verify_token_set ? (
                                            <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]">
                                                <CheckCircle2 size={12} /> Terkonfigurasi (lihat META_WA_VERIFY_TOKEN di .env)
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-destructive">
                                                <AlertCircle size={12} /> Belum di-set
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-auto max-w-7xl px-5 py-6 grid gap-5 md:grid-cols-[320px_1fr]">
                {/* Left: conversation list */}
                <aside className="rounded-xl border border-border bg-white overflow-hidden">
                    <div className="border-b border-border px-4 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-2">
                        <MessageCircle size={14} strokeWidth={1.6} /> Percakapan ({conversations.length})
                    </div>
                    <div className="border-b border-border p-2.5 space-y-2 bg-secondary/30">
                        <input
                            data-testid="conv-search-input"
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            placeholder="Cari nomor / nama / pesan..."
                            className="w-full rounded-md border border-border bg-white px-3 py-1.5 text-xs"
                        />
                        <div className="flex flex-wrap gap-1">
                            {[
                                { k: "all", l: "Semua" },
                                { k: "open", l: "Terbuka" },
                                { k: "escalated", l: "🔥 Eskalasi" },
                                { k: "resolved", l: "Selesai" },
                            ].map((t) => (
                                <button
                                    key={t.k}
                                    data-testid={`conv-filter-${t.k}`}
                                    onClick={() => setStatusFilter(t.k)}
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-medium border transition-colors ${statusFilter === t.k ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:bg-secondary"}`}
                                >{t.l}</button>
                            ))}
                        </div>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center"><Loader2 className="inline animate-spin text-primary" size={20} /></div>
                    ) : conversations.length === 0 ? (
                        <div data-testid="conversations-empty" className="p-8 text-center text-sm text-muted-foreground">
                            Belum ada percakapan.<br />
                            Tes dengan kirim WhatsApp ke nomor bisnis Anda.
                        </div>
                    ) : (
                        <ul className="max-h-[70vh] overflow-y-auto">
                            {conversations.map((c) => (
                                <li
                                    key={c.conversation_id}
                                    onClick={() => loadDetail(c.conversation_id)}
                                    data-testid={`conv-${c.conversation_id}`}
                                    className={`cursor-pointer border-b border-border px-4 py-3 transition-colors hover:bg-secondary/50 ${selected === c.conversation_id ? "bg-secondary" : ""}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-medium text-sm truncate">
                                            {c.conversation_id.replace("whatsapp:", "")}
                                        </div>
                                        {c.needs_human && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                                                <AlertCircle size={10} strokeWidth={2} /> Perlu Admin
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 truncate text-xs text-muted-foreground">
                                        {c.last_direction === "outbound" ? "🤖 " : "👤 "}
                                        {c.last_message}
                                    </div>
                                    <div className="mt-1 text-[10px] text-muted-foreground">
                                        {new Date(c.last_at).toLocaleString("id-ID", { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                                        {" · "}{c.message_count} pesan
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                {/* Right: messages + reply */}
                <section className="rounded-xl border border-border bg-white flex flex-col min-h-[70vh]">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-center p-12">
                            <div className="text-muted-foreground">
                                <MessageCircle size={32} strokeWidth={1.25} className="mx-auto" />
                                <p className="mt-3 text-sm">Pilih percakapan untuk lihat detail.</p>
                            </div>
                        </div>
                    ) : !detail ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Loader2 className="animate-spin text-primary" size={22} />
                        </div>
                    ) : (
                        <>
                            <div className="border-b border-border px-5 py-3 flex items-center justify-between">
                                <div>
                                    <div className="font-heading font-semibold">{selected.replace("whatsapp:", "")}</div>
                                    <div className="text-xs text-muted-foreground">{detail.summary?.message_count} pesan</div>
                                </div>
                                {detail.summary?.needs_human && (
                                    <button
                                        onClick={markResolved}
                                        data-testid="mark-resolved-btn"
                                        className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success))] bg-[hsl(var(--success))]/10 px-3 py-1.5 text-xs text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15 transition-colors"
                                    >
                                        <CheckCircle2 size={12} /> Tandai Selesai
                                    </button>
                                )}
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 space-y-4">
                                {detail.messages.map((m, i) => (
                                    <div
                                        key={`${m.message_sid || i}-${i}`}
                                        data-testid={`msg-${i}`}
                                        className={`flex gap-2.5 ${m.direction === "outbound" ? "flex-row-reverse" : ""}`}
                                    >
                                        <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${m.direction === "outbound" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                                            {m.direction === "outbound" ? <Bot size={14} strokeWidth={1.75} /> : <User size={14} strokeWidth={1.75} />}
                                        </div>
                                        <div className={`max-w-[75%] rounded-lg px-3.5 py-2 text-sm whitespace-pre-line ${m.direction === "outbound" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                                            {m.body}
                                            <div className={`mt-1.5 text-[10px] opacity-70 ${m.direction === "outbound" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                                                {new Date(m.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                                                {m.escalated ? " · ↪ admin" : ""}
                                                {m.handled_by ? ` · by ${m.handled_by}` : (m.direction === "outbound" && m.source !== "manual" ? " · 🤖 AI" : "")}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-border p-4 bg-[hsl(var(--muted))]">
                                <p className="mb-2 text-[11px] text-muted-foreground">
                                    Balas manual sebagai admin. Pesan ini akan dikirim langsung ke customer via WhatsApp (bypass AI).
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        data-testid="manual-reply-input"
                                        value={reply}
                                        onChange={(e) => setReply(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && sendReply()}
                                        placeholder="Ketik balasan…"
                                        className="flex-1 rounded-full border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                                    />
                                    <button
                                        onClick={sendReply}
                                        disabled={sending || !reply.trim()}
                                        data-testid="send-manual-reply-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-all duration-200"
                                    >
                                        {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} strokeWidth={1.75} />} Kirim
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
